import axios from "axios";
import CreativeSet from "@/domain/CreativeSet";
import BackEndpoints from "@/router/endpoints/BackEndpoints";
import ToastService from "@/services/ToastService";
import ErrorUtils from "@/utils/ErrorUtils";
import BrandService from "@/services/BrandService";

export default {
  async list() {
    try {
      let brand = await BrandService.getDefaultBrand();
      let resp = await axios.get(BackEndpoints.creativeSets(brand.id));
      const respSort = resp.data.creativeSets.sort((creativeSet1, creativeSet2) => creativeSet2.id.localeCompare(creativeSet1.id));
      return respSort.map((creativeSet) => {
        return new CreativeSet(creativeSet);
      });
    } catch (error) {
      ToastService.error("Error when loading creative sets", ErrorUtils.getMessage(error));
      throw error;
    }
  },
  async listExternal(token) {
    try {
      let resp = await axios.get(
        BackEndpoints.withParams(BackEndpoints.creativeSetIndexExternal, {
          max: 1000,
        }),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const respSort = resp.data.sort((creativeSet1, creativeSet2) => creativeSet2.id.localeCompare(creativeSet1.id));

      return respSort.map((creativeSet) => {
        return new CreativeSet(creativeSet);
      });
    } catch (error) {
      ToastService.error("Error when loading creative sets", ErrorUtils.getMessage(error));
      throw error;
    }
  },
  async get(creativeSetId) {
    try {
      let brand = await BrandService.getDefaultBrand();
      let resp = await axios.get(BackEndpoints.creativeSet(brand.id, creativeSetId));
      return new CreativeSet(resp.data);
    } catch (error) {
      ToastService.error("Error when loading creative set", ErrorUtils.getMessage(error));
      throw error;
    }
  },
  async delete(creativeSetId) {
    try {
      let brand = await BrandService.getDefaultBrand();
      await axios.delete(BackEndpoints.creativeSet(brand.id, creativeSetId));
      ToastService.success("Success", "The creative set has been deleted.");
    } catch (error) {
      ToastService.error("Error when deleting creative set", ErrorUtils.getMessage(error));
      throw error;
    }
  },
  async create(creativeSet) {
    try {
      let brand = await BrandService.getDefaultBrand();
      let resp = await axios.post(BackEndpoints.creativeSets(brand.id), creativeSet);
      ToastService.success("Success", "The creative set has been created.");
      return new CreativeSet(resp.data);
    } catch (error) {
      ToastService.error("Error when creating creative set", ErrorUtils.getMessage(error));
      throw error;
    }
  },
  async update(creativeSet) {
    try {
      let brand = await BrandService.getDefaultBrand();
      let resp = await axios.put(BackEndpoints.creativeSet(brand.id, creativeSet.id), creativeSet);
      ToastService.success("Success", "The creative set has been updated.");
      return new CreativeSet(resp.data);
    } catch (error) {
      ToastService.error("Error when updating creative set", ErrorUtils.getMessage(error));
      throw error;
    }
  },
  async getAssetsIntoDb(spreadsheetUrl, creativeSetId, markets) {
    const resp = await axios.post(
      BackEndpoints.getCreativeSetsData,
      {
        markets: markets,
      },
      {
        params: {
          spreadsheetUrl: spreadsheetUrl,
          creativeSetId: creativeSetId,
        },
      }
    );
    if (resp.status === 200 && resp?.data?.warnings?.length) {
      ToastService.warning("Warnings", resp.data.warnings, { copyBtn: true });
    }
  },
  async getCSV(creativeSetId) {
    return await axios.get(BackEndpoints.downloadCsv, {
      params: {
        creativeSetId: creativeSetId,
      },
      responseType: "blob",
    });
  },
  async getShareUrl(market = null, creativeSetId = null) {
    try {
      let data = {};
      if (market) {
        data.market = market;
      }
      if (creativeSetId) {
        data.creativeSetId = creativeSetId;
      }
      let resp = await axios.get(BackEndpoints.withParams(BackEndpoints.shareUrl, data));
      return resp.data.url;
    } catch (error) {
      ToastService.error("Error when creating creative set", ErrorUtils.getMessage(error));
      throw error;
    }
  },
  async readToken(token) {
    try {
      let resp = await axios.get(BackEndpoints.readToken, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return resp.data;
    } catch (e) {
      ToastService.error("Error reading authorization token.");
    }
  },
  async checkSpreadsheetUrl(spreadsheetUrl, currentId) {
    try {
      let resp = await axios.get(BackEndpoints.checkSpreadsheetUrl, {
        params: {
          spreadsheetUrl: spreadsheetUrl,
          currentId: currentId,
        },
      });
      return resp.data;
    } catch (e) {
      ToastService.error("Error checking spreadsheet url.");
    }
  },
  async getMarkets(creativeSetId) {
    let brand = await BrandService.getDefaultBrand();
    const resp = await axios.get(
      BackEndpoints.creativeSet(brand.id, creativeSetId) + "/markets"
    );
    return resp.data;
  }
};
